import { WppActionButton, WppButton, WppInput, WppTypography } from '@wppopen/components-library-react'
import styles from './VariantDataForm.module.scss'
import FormAccordionSection from 'components/FormAccordionSection/FormAccordionSection'
import { getType } from 'utils'
import { StateToSingleInputFieldMap } from 'reducers/FormReducer/FormReducer'
import { VariantsDetailData, VariantsPartialUpdateData } from '@contracts'
import { Dispatch, SetStateAction, useMemo, useReducer, useState } from 'react'
import editVariantFormReducer, {
  defaultEditVariantErrorObject,
  editVariantFormHelperFunctions,
  EditVariantFormReducerState,
  initialEditVariantFormReducerState,
} from 'reducers/EditVariantReducer/EditVariantReducer'
import { renderInputIcon, renderMessage, renderMessageType } from '../form-utils'
import { WppInputCustomEvent } from '@wppopen/components-library/dist/types/components'
import { InputChangeEventDetail } from '@wppopen/components-library'
import { ERROR_MESSAGES } from 'utils/constants'
import {
  attributesFields,
  mainRequiredFields,
  renderVariantAttributesIcon,
  renderVariantAttributesMessage,
  renderVariantAttributesMessageType,
} from './utils'
import { GenericModal } from 'components/Modals'
import useWarningChanging from 'hooks/useWarningChanging'

interface VariantDataFormProps {
  variantData: VariantsDetailData
  isFormActive: boolean
  areActionsVisible?: boolean
  isPatchVariantLoading: boolean
  setHasUnsavedChanges?: Dispatch<SetStateAction<boolean>>
  onFormEdit?: () => void
  onFormCancel?: () => void
  onFormSubmit?: (data: VariantsPartialUpdateData) => Promise<void>
}

export default function VariantDataForm({
  variantData,
  isFormActive,
  areActionsVisible,
  setHasUnsavedChanges,
  isPatchVariantLoading,
  onFormEdit,
  onFormCancel,
  onFormSubmit,
}: VariantDataFormProps) {
  const [isErrorForm, setIsErrorForm] = useState(false)
  const [isVariantDataGood, setIsVariantDataGood] = useState(false)
  const initialVariantData = useMemo(() => initialEditVariantFormReducerState(variantData), [variantData])
  const formReducer = useReducer(editVariantFormReducer, initialVariantData)
  const [formReducerState, formReducerDispatch] = formReducer
  const {
    reference_name,
    variant_id,
    reference_image_url,
    quantity,
    size,
    colour,
    flavour,
    format,
    formulation,
    product_dimensions,
    pack_count,
    model_number,
  } = formReducerState
  const requiredFields = useMemo(() => {
    // Extract the requiredField array from defaultEditProductErrorObject
    const requiredFields = defaultEditVariantErrorObject.requiredField || []
    // Map through the array to get an array of IDs
    const ids = requiredFields.map(({ id }) => id)
    // Filter out duplicate IDs by checking their first occurrence index
    const uniqueIds = ids.filter((id, index, array) => array.indexOf(id) === index)
    // If there are no unique IDs, return an empty array
    return uniqueIds
  }, [defaultEditVariantErrorObject.requiredField])
  const isOneAttributeFilled = useMemo(() => {
    const attributes = [
      quantity,
      size,
      colour,
      flavour,
      format,
      formulation,
      product_dimensions,
      pack_count,
      model_number,
    ]
    return attributes.some(({ value }) => !!value)
  }, [quantity, size, colour, flavour, format, formulation, product_dimensions])
  const { resetForm, updateSingleInput: updateInput, handleError } = editVariantFormHelperFunctions

  // unsaved changes section
  const isFormUnsaved = useMemo(() => {
    let hasUnsavedChanges = false

    // only when form is active because when the form is not active or it gets out of edit mode it should reset the changes
    if (isFormActive) {
      hasUnsavedChanges = Object.keys(initialVariantData).some(key => {
        const id = key as keyof EditVariantFormReducerState
        return JSON.stringify(initialVariantData[id].value) !== JSON.stringify(formReducerState[id].value)
      })
    }

    return hasUnsavedChanges
  }, [isFormActive, initialVariantData, formReducerState])
  useWarningChanging(isFormUnsaved, setHasUnsavedChanges)

  // form actions section
  const onInputChange = (e: WppInputCustomEvent<InputChangeEventDetail>) => {
    const { target } = e
    const { value, id: key } = target
    const id = key as keyof EditVariantFormReducerState

    formReducerDispatch(updateInput(key as StateToSingleInputFieldMap<'EditVariantFormReducerState'>, value))
    // if id matches the required fields and there is error then remove the error
    const isRequiredField = requiredFields.includes(id)
    if (isRequiredField && formReducerState[id].error) {
      formReducerDispatch(handleError(id, undefined))
    } else if (isRequiredField && !value) {
      formReducerDispatch(handleError(id, ERROR_MESSAGES.ESSENTIAL_FIELD))
    }
  }

  const handleFormSubmit = () => {
    let hasErrors = true
    // check all required fields values and if they are empty, dispatch the error
    mainRequiredFields.forEach(field => {
      const id = field as keyof EditVariantFormReducerState
      if (!formReducerState[id].value) {
        hasErrors = false
        formReducerDispatch(handleError(id, ERROR_MESSAGES.ESSENTIAL_FIELD))
      }
    })

    if (!hasErrors) {
      setIsErrorForm(true)
    }
    if (!hasErrors || !isFormUnsaved) return

    setIsVariantDataGood(true)
  }

  const handleSaveProduct = async () => {
    const newVariantData: VariantsPartialUpdateData = {
      reference_name: reference_name.value,
      variant_id: variant_id.value,
      reference_image_url: reference_image_url.value,
      attributes: [],
      product_id: variantData.product_id,
    }

    // add attributes to the newVariantData
    attributesFields.forEach(field => {
      const id = field as keyof EditVariantFormReducerState
      if (formReducerState[id].value) {
        newVariantData.attributes?.push({
          name: field,
          value: formReducerState[id].value as string,
        })
      }
    })

    // update data
    try {
      if (onFormSubmit) await onFormSubmit(newVariantData)
      setIsVariantDataGood(false)
    } catch (err) {
      throw err
    }
  }

  return (
    <div className={styles.formContainer}>
      <form>
        <div className={styles.actions}>
          {areActionsVisible && (
            <>
              {isFormActive ? (
                <>
                  <WppActionButton
                    data-testid="cancel"
                    onClick={() => {
                      formReducerDispatch(resetForm(initialVariantData))
                      if (onFormCancel) onFormCancel()
                    }}
                  >
                    Cancel
                  </WppActionButton>
                  <WppButton onClick={handleFormSubmit} data-testid="save-edit">
                    Save edits
                  </WppButton>
                </>
              ) : (
                <WppButton onClick={onFormEdit} data-testid="edit">
                  Edit
                </WppButton>
              )}
            </>
          )}
        </div>

        <div className={styles.formInner}>
          <div className={styles.halfWidth}>
            <WppInput
              labelConfig={{
                text: 'Reference name',
                icon: !isFormActive ? undefined : 'wpp-icon-info',
                description: 'Used internally to quickly find the product. This does not get used for AI generation.',
              }}
              data-testid="reference-name"
              required
              id={getType<StateToSingleInputFieldMap<'EditVariantFormReducerState'>>('reference_name')}
              value={reference_name.value}
              disabled={!isFormActive}
              onWppChange={onInputChange}
              messageType={renderMessageType(isFormActive, reference_name.value, reference_name.error)}
              message={renderMessage(isFormActive, reference_name.value, reference_name.error)}
            >
              {renderInputIcon(reference_name.value, reference_name.error)}
            </WppInput>
            <WppInput
              labelConfig={{
                text: 'Variant ID',
                icon: !isFormActive ? undefined : 'wpp-icon-info',
                description:
                  'Unique ID, used to identify a specific product. EAN or most appropriate ID can be used here.',
              }}
              data-testid="variant-id"
              required
              id={getType<StateToSingleInputFieldMap<'EditVariantFormReducerState'>>('variant_id')}
              value={variant_id.value}
              disabled={!isFormActive}
              onWppChange={onInputChange}
              messageType={renderMessageType(isFormActive, variant_id.value, variant_id.error)}
              message={renderMessage(isFormActive, variant_id.value, variant_id.error)}
            >
              {renderInputIcon(variant_id.value, variant_id.error)}
            </WppInput>
          </div>
          <WppInput
            labelConfig={{ text: 'Image URL' }}
            data-testid="image-url"
            id={getType<StateToSingleInputFieldMap<'EditVariantFormReducerState'>>('reference_image_url')}
            value={reference_image_url.value}
            disabled={!isFormActive}
            onWppChange={onInputChange}
            messageType={reference_image_url.error ? 'error' : undefined}
            message={reference_image_url.error}
          />
          <FormAccordionSection
            text="Variant attributes (at least one is required)"
            tooltip="Use to identify attributes specific to the variant product."
            isFormActive={isFormActive}
            halfWidthChildren={
              <>
                <WppInput
                  labelConfig={{ text: 'Quantity' }}
                  data-testid="quantity"
                  id={getType<StateToSingleInputFieldMap<'EditVariantFormReducerState'>>('quantity')}
                  required
                  value={quantity.value}
                  disabled={!isFormActive}
                  onWppChange={onInputChange}
                  messageType={renderVariantAttributesMessageType(
                    isFormActive,
                    quantity.value,
                    quantity.error,
                    isOneAttributeFilled,
                  )}
                  message={renderVariantAttributesMessage(
                    isFormActive,
                    quantity.value,
                    quantity.error,
                    isOneAttributeFilled,
                  )}
                >
                  {renderVariantAttributesIcon(quantity.value, quantity.error, isOneAttributeFilled)}
                </WppInput>
                <WppInput
                  labelConfig={{ text: 'Size' }}
                  data-testid="size"
                  required
                  id={getType<StateToSingleInputFieldMap<'EditVariantFormReducerState'>>('size')}
                  value={size.value}
                  disabled={!isFormActive}
                  onWppChange={onInputChange}
                  messageType={renderVariantAttributesMessageType(
                    isFormActive,
                    size.value,
                    size.error,
                    isOneAttributeFilled,
                  )}
                  message={renderVariantAttributesMessage(isFormActive, size.value, size.error, isOneAttributeFilled)}
                >
                  {renderVariantAttributesIcon(size.value, size.error, isOneAttributeFilled)}
                </WppInput>
                <WppInput
                  labelConfig={{ text: 'Colour' }}
                  data-testid="colour"
                  required
                  id={getType<StateToSingleInputFieldMap<'EditVariantFormReducerState'>>('colour')}
                  value={colour.value}
                  disabled={!isFormActive}
                  onWppChange={onInputChange}
                  messageType={renderVariantAttributesMessageType(
                    isFormActive,
                    colour.value,
                    colour.error,
                    isOneAttributeFilled,
                  )}
                  message={renderVariantAttributesMessage(
                    isFormActive,
                    colour.value,
                    colour.error,
                    isOneAttributeFilled,
                  )}
                >
                  {renderVariantAttributesIcon(colour.value, colour.error, isOneAttributeFilled)}
                </WppInput>
                <WppInput
                  labelConfig={{ text: 'Flavour' }}
                  data-testid="flavour"
                  required
                  id={getType<StateToSingleInputFieldMap<'EditVariantFormReducerState'>>('flavour')}
                  value={flavour.value}
                  disabled={!isFormActive}
                  onWppChange={onInputChange}
                  messageType={renderVariantAttributesMessageType(
                    isFormActive,
                    flavour.value,
                    flavour.error,
                    isOneAttributeFilled,
                  )}
                  message={renderVariantAttributesMessage(
                    isFormActive,
                    flavour.value,
                    flavour.error,
                    isOneAttributeFilled,
                  )}
                >
                  {renderVariantAttributesIcon(flavour.value, flavour.error, isOneAttributeFilled)}
                </WppInput>
                <WppInput
                  labelConfig={{ text: 'Format' }}
                  data-testid="format"
                  required
                  id={getType<StateToSingleInputFieldMap<'EditVariantFormReducerState'>>('format')}
                  value={format.value}
                  disabled={!isFormActive}
                  onWppChange={onInputChange}
                  messageType={renderVariantAttributesMessageType(
                    isFormActive,
                    format.value,
                    format.error,
                    isOneAttributeFilled,
                  )}
                  message={renderVariantAttributesMessage(
                    isFormActive,
                    format.value,
                    format.error,
                    isOneAttributeFilled,
                  )}
                >
                  {renderVariantAttributesIcon(format.value, format.error, isOneAttributeFilled)}
                </WppInput>
                <WppInput
                  labelConfig={{ text: 'Formulation' }}
                  data-testid="formulation"
                  required
                  id={getType<StateToSingleInputFieldMap<'EditVariantFormReducerState'>>('formulation')}
                  value={formulation.value}
                  disabled={!isFormActive}
                  onWppChange={onInputChange}
                  messageType={renderVariantAttributesMessageType(
                    isFormActive,
                    formulation.value,
                    formulation.error,
                    isOneAttributeFilled,
                  )}
                  message={renderVariantAttributesMessage(
                    isFormActive,
                    formulation.value,
                    formulation.error,
                    isOneAttributeFilled,
                  )}
                >
                  {renderVariantAttributesIcon(formulation.value, formulation.error, isOneAttributeFilled)}
                </WppInput>
                <WppInput
                  labelConfig={{ text: 'Product dimensions' }}
                  data-testid="product-dimensions"
                  required
                  id={getType<StateToSingleInputFieldMap<'EditVariantFormReducerState'>>('product_dimensions')}
                  value={product_dimensions.value}
                  disabled={!isFormActive}
                  onWppChange={onInputChange}
                  messageType={renderVariantAttributesMessageType(
                    isFormActive,
                    product_dimensions.value,
                    product_dimensions.error,
                    isOneAttributeFilled,
                  )}
                  message={renderVariantAttributesMessage(
                    isFormActive,
                    product_dimensions.value,
                    product_dimensions.error,
                    isOneAttributeFilled,
                  )}
                >
                  {renderVariantAttributesIcon(
                    product_dimensions.value,
                    product_dimensions.error,
                    isOneAttributeFilled,
                  )}
                </WppInput>
                <WppInput
                  labelConfig={{ text: 'Pack count' }}
                  data-testid="pack-count"
                  required
                  id={getType<StateToSingleInputFieldMap<'EditVariantFormReducerState'>>('pack_count')}
                  value={pack_count.value}
                  disabled={!isFormActive}
                  onWppChange={onInputChange}
                  messageType={renderVariantAttributesMessageType(
                    isFormActive,
                    pack_count.value,
                    pack_count.error,
                    isOneAttributeFilled,
                  )}
                  message={renderVariantAttributesMessage(
                    isFormActive,
                    pack_count.value,
                    pack_count.error,
                    isOneAttributeFilled,
                  )}
                >
                  {renderVariantAttributesIcon(pack_count.value, pack_count.error, isOneAttributeFilled)}
                </WppInput>
                <WppInput
                  labelConfig={{ text: 'Model number' }}
                  data-testid="model-number"
                  required
                  id={getType<StateToSingleInputFieldMap<'EditVariantFormReducerState'>>('model_number')}
                  value={model_number.value}
                  disabled={!isFormActive}
                  onWppChange={onInputChange}
                  messageType={renderVariantAttributesMessageType(
                    isFormActive,
                    model_number.value,
                    model_number.error,
                    isOneAttributeFilled,
                  )}
                  message={renderVariantAttributesMessage(
                    isFormActive,
                    model_number.value,
                    model_number.error,
                    isOneAttributeFilled,
                  )}
                >
                  {renderVariantAttributesIcon(model_number.value, model_number.error, isOneAttributeFilled)}
                </WppInput>
              </>
            }
          />
        </div>
      </form>

      {isVariantDataGood && (
        <GenericModal
          onCancel={() => setIsVariantDataGood(false)}
          onSubmit={handleSaveProduct}
          modalTitle="Save edits"
          submitButtonText="Save"
          shouldModalOpen={isVariantDataGood}
          isApiLoading={isPatchVariantLoading}
        >
          <WppTypography tag="p" type="s-body" data-testid="success-modal-disclaimer">
            Are you sure you want to save these edits to this variant?
          </WppTypography>
        </GenericModal>
      )}

      {isErrorForm && (
        <GenericModal
          onSubmit={async () => setIsErrorForm(false)}
          modalTitle="Error saving"
          submitButtonText="Ok"
          shouldModalOpen={isErrorForm}
          isApiLoading={false}
        >
          <WppTypography tag="p" type="s-body" data-testid="error-modal-disclaimer">
            You have mandatory data to input before you are able to save this variant.
          </WppTypography>
        </GenericModal>
      )}
    </div>
  )
}
